import type {ReactNode, ReactElement, RefObject} from 'react'
import React, {Children, isValidElement, Fragment} from 'react'
import {SoftNavOnlyLink as Link} from '@github-ui/react-router'
import {ActionMenu, ActionList} from '@primer/react'

import styles from './Breadcrumbs.module.css'
import {TriangleDownIcon} from '@primer/octicons-react'
import {SkeletonText} from '@primer/react/experimental'
type ItemProps = {
  href?: string
  children: ReactNode
  loading?: boolean
} & Omit<React.ComponentProps<typeof Link>, 'to'>

type LastItemProps = {
  buttonRef?: RefObject<HTMLButtonElement | null>
  children: ReactNode
  menuOverlay: ReactElement
}

type BreadcrumbsProps = {
  children: ReactNode
  'aria-label'?: string
  condensed?: boolean
}

type BreadcrumbItem = {
  key: string
  href?: string
  children: ReactNode
  hasMenu?: boolean
  menuOverlay?: ReactElement
}

/* <Breadcrumbs> */
const Breadcrumbs = ({children, 'aria-label': ariaLabel = 'Breadcrumbs', condensed}: BreadcrumbsProps) => {
  const items: BreadcrumbItem[] = []

  // Recursive function to extract items from children, handling fragments
  const extractItems = (node: ReactNode, keyPrefix = '') => {
    const childrenArray = Children.toArray(node)

    for (const [index, child] of childrenArray.entries()) {
      if (isValidElement(child)) {
        // Handle React Fragments
        if (child.type === Fragment || child.type.toString() === 'Symbol(react.fragment)') {
          const fragmentChild = child as ReactElement<React.ComponentProps<typeof Fragment>>
          extractItems(fragmentChild.props.children, `${keyPrefix}fragment-${index}-`)
        } else if (child.type === Item) {
          const itemChild = child as ReactElement<React.ComponentProps<typeof Item>>
          items.push({
            key: itemChild.key?.toString() || `${keyPrefix}item-${index}`,
            href: itemChild.props.href,
            children: itemChild.props.children,
            hasMenu: false,
          })
        } else if (child.type === ItemWithMenu) {
          const itemWithMenuChild = child as ReactElement<React.ComponentProps<typeof ItemWithMenu>>
          items.push({
            key: itemWithMenuChild.key?.toString() || `${keyPrefix}item-${index}`,
            children: itemWithMenuChild.props.children,
            hasMenu: true,
            menuOverlay: itemWithMenuChild.props.menuOverlay,
          })
        }
      }
    }
  }

  extractItems(children)

  const lastItem = items[items.length - 1]
  const previousItems = items.slice(0, -1)
  const canCondense = items.length >= 3
  const showCondensed = condensed !== undefined ? condensed && canCondense : false
  const triggerRef = React.useRef<HTMLButtonElement>(null)

  if (condensed !== undefined) {
    if (showCondensed) {
      return (
        <nav className={`${styles.container} ${styles.condensed}`} aria-label={ariaLabel}>
          <ol className={styles.list}>
            <li className={styles.item}>
              <ActionMenu>
                <ActionMenu.Button ref={triggerRef} className={styles.link}>
                  ...
                </ActionMenu.Button>
                <ActionMenu.Overlay>
                  <ActionList>
                    {previousItems.map(item => (
                      <ActionList.LinkItem key={item.key} href={item.href}>
                        {item.children}
                      </ActionList.LinkItem>
                    ))}
                  </ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </li>
            {lastItem &&
              (lastItem.hasMenu && lastItem.menuOverlay ? (
                <ItemWithMenu menuOverlay={lastItem.menuOverlay}>{lastItem.children}</ItemWithMenu>
              ) : lastItem.href ? (
                <Item href={lastItem.href}>{lastItem.children}</Item>
              ) : null)}
          </ol>
        </nav>
      )
    } else {
      return (
        <nav className={styles.container} aria-label={ariaLabel}>
          <ol className={styles.list}>{children}</ol>
        </nav>
      )
    }
  }

  // Adding this for the moment until we can control from the outside. The media query approach (both versions in DOM)
  // can be removed once prop is used.

  return (
    <nav className={styles.container} aria-label={ariaLabel}>
      {/* Full */}
      <ol className={`${styles.list} ${canCondense ? styles.fullList : styles.alwaysVisible}`}>{children}</ol>

      {/* Condensed */}
      {canCondense && (
        <ol className={`${styles.list} ${styles.condensedList}`}>
          <li className={styles.item}>
            <ActionMenu>
              <ActionMenu.Button ref={triggerRef} className={styles.link}>
                ...
              </ActionMenu.Button>
              <ActionMenu.Overlay>
                <ActionList>
                  {previousItems.map(item => (
                    <ActionList.LinkItem key={item.key} href={item.href}>
                      {item.children}
                    </ActionList.LinkItem>
                  ))}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          </li>
          {lastItem &&
            (lastItem.hasMenu && lastItem.menuOverlay ? (
              <ItemWithMenu menuOverlay={lastItem.menuOverlay}>{lastItem.children}</ItemWithMenu>
            ) : lastItem.href ? (
              <Item href={lastItem.href}>{lastItem.children}</Item>
            ) : null)}
        </ol>
      )}
    </nav>
  )
}

/* <Breadcrumbs.Item> */
const Item = ({href, loading, children, ...rest}: ItemProps) => {
  if (loading) {
    return (
      <li className={styles.item}>
        <SkeletonText className={styles.skeleton} />
      </li>
    )
  }

  if (href) {
    return (
      <li className={styles.item}>
        <Link {...rest} to={href} className={styles.link} aria-current="page">
          <span className={styles.text}>{children}</span>
        </Link>
      </li>
    )
  }
  return (
    <li className={styles.item}>
      <span className={styles.textOnly}>{children}</span>
    </li>
  )
}

/* <Breadcrumbs.ItemWithMenu> */
const ItemWithMenu = ({buttonRef, children, menuOverlay}: LastItemProps) => {
  return (
    <li className={styles.item}>
      <ActionMenu anchorRef={buttonRef}>
        <ActionMenu.Anchor>
          <button className={styles.link} aria-current="page" aria-haspopup="true">
            <span className={styles.text}>{children}</span>
            <span className={styles.icon}>
              <TriangleDownIcon />
            </span>
          </button>
        </ActionMenu.Anchor>
        {menuOverlay}
      </ActionMenu>
    </li>
  )
}

Breadcrumbs.Item = Item
Breadcrumbs.ItemWithMenu = ItemWithMenu

export {Breadcrumbs}
